/**
 * Get browser's local timezone
 * @returns {string} IANA timezone string (e.g., 'Europe/Berlin')
 */
export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return 'Europe/Kiev'; // Fallback for older browsers
  }
};

// Platform timezone for admin operations (fixed Kiev time)
export const PLATFORM_TIMEZONE = 'Europe/Kiev';

// Default timezone uses browser's local timezone
export const DEFAULT_TIMEZONE = getBrowserTimezone();

// Common timezones for the dropdown
export const TIMEZONES = [
  { value: 'Europe/Kiev', label: 'Київ (UTC+2/+3)', country: 'Україна' },
  { value: 'Europe/Berlin', label: 'Берлін (UTC+1/+2)', country: 'Німеччина' },
  { value: 'Europe/London', label: 'Лондон (UTC+0/+1)', country: 'Велика Британія' },
  { value: 'Europe/Paris', label: 'Париж (UTC+1/+2)', country: 'Франція' },
  { value: 'Europe/Rome', label: 'Рим (UTC+1/+2)', country: 'Італія' },
  { value: 'Europe/Madrid', label: 'Мадрид (UTC+1/+2)', country: 'Іспанія' },
  { value: 'Europe/Warsaw', label: 'Варшава (UTC+1/+2)', country: 'Польща' },
  { value: 'Europe/Prague', label: 'Прага (UTC+1/+2)', country: 'Чехія' },
  { value: 'Europe/Vienna', label: 'Відень (UTC+1/+2)', country: 'Австрія' },
  { value: 'Europe/Amsterdam', label: 'Амстердам (UTC+1/+2)', country: 'Нідерланди' },
  { value: 'Europe/Brussels', label: 'Брюссель (UTC+1/+2)', country: 'Бельгія' },
  { value: 'Europe/Lisbon', label: 'Лісабон (UTC+0/+1)', country: 'Португалія' },
  { value: 'Europe/Stockholm', label: 'Стокгольм (UTC+1/+2)', country: 'Швеція' },
  { value: 'Europe/Oslo', label: 'Осло (UTC+1/+2)', country: 'Норвегія' },
  { value: 'Europe/Helsinki', label: 'Гельсінкі (UTC+2/+3)', country: 'Фінляндія' },
  { value: 'Europe/Athens', label: 'Афіни (UTC+2/+3)', country: 'Греція' },
  { value: 'Europe/Bucharest', label: 'Бухарест (UTC+2/+3)', country: 'Румунія' },
  { value: 'Europe/Sofia', label: 'Софія (UTC+2/+3)', country: 'Болгарія' },
  { value: 'Europe/Istanbul', label: 'Стамбул (UTC+3)', country: 'Туреччина' },
  { value: 'America/New_York', label: 'Нью-Йорк (UTC-5/-4)', country: 'США' },
  { value: 'America/Los_Angeles', label: 'Лос-Анджелес (UTC-8/-7)', country: 'США' },
  { value: 'America/Chicago', label: 'Чикаго (UTC-6/-5)', country: 'США' },
  { value: 'America/Toronto', label: 'Торонто (UTC-5/-4)', country: 'Канада' },
  { value: 'Asia/Dubai', label: 'Дубай (UTC+4)', country: 'ОАЕ' },
  { value: 'Asia/Jerusalem', label: 'Єрусалим (UTC+2/+3)', country: 'Ізраїль' },
];

// Ukrainian month names
const monthNamesGenitive = [
  'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
  'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'
];

const monthNames = [
  'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'
];

const dayNames = ['Неділя', 'Понеділок', 'Вівторок', 'Середа', 'Четвер', 'П\'ятниця', 'Субота'];
const dayAbbreviations = ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
const dayAbbreviationsMondayFirst = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];

/**
 * Parse date string as UTC
 * PostgreSQL TIMESTAMP returns dates without timezone info (e.g., "2026-04-22T17:00:00")
 * JavaScript interprets these as local time, so we need to ensure they're treated as UTC
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {Date} Date object
 */
export const parseAsUTC = (dateString) => {
  if (dateString instanceof Date) return dateString;
  if (!dateString) return new Date();

  // If already has timezone indicator (Z or +/-offset), parse directly
  if (dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)) {
    return new Date(dateString);
  }

  // Append Z to interpret as UTC
  return new Date(dateString + 'Z');
};

/**
 * Format time in user's timezone
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {string} timezone - IANA timezone string (e.g., 'Europe/Kiev')
 * @returns {string} Formatted time (e.g., '14:30')
 */
export const formatTime = (dateString, timezone = DEFAULT_TIMEZONE) => {
  if (!dateString) return '';
  const date = parseAsUTC(dateString);
  return date.toLocaleTimeString('uk-UA', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone
  });
};

/**
 * Format date in Ukrainian style with user's timezone
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted date (e.g., '15 березня')
 */
export const formatDateUkrainian = (dateString, timezone = DEFAULT_TIMEZONE) => {
  if (!dateString) return '';
  const date = parseAsUTC(dateString);

  // Get date parts in the target timezone
  const formatter = new Intl.DateTimeFormat('uk-UA', {
    day: 'numeric',
    month: 'numeric',
    timeZone: timezone
  });
  const parts = formatter.formatToParts(date);
  const day = parts.find(p => p.type === 'day')?.value;
  const month = parseInt(parts.find(p => p.type === 'month')?.value) - 1;

  return `${day} ${monthNamesGenitive[month]}`;
};

/**
 * Format full date with time in user's timezone
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted date and time (e.g., '15 березня, 14:30')
 */
export const formatDateTime = (dateString, timezone = DEFAULT_TIMEZONE) => {
  if (!dateString) return '';
  return `${formatDateUkrainian(dateString, timezone)}, ${formatTime(dateString, timezone)}`;
};

/**
 * Get day of week name in Ukrainian
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {string} timezone - IANA timezone string
 * @returns {string} Day name (e.g., 'Понеділок')
 */
export const getDayOfWeek = (dateString, timezone = DEFAULT_TIMEZONE) => {
  if (!dateString) return '';
  const date = parseAsUTC(dateString);

  // Get day of week in the target timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    timeZone: timezone
  });
  const dayStr = formatter.format(date);
  const dayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
  return dayNames[dayMap[dayStr]] || '';
};

/**
 * Get abbreviated day of week in Ukrainian
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {string} timezone - IANA timezone string
 * @returns {string} Abbreviated day name (e.g., 'Пн')
 */
export const getDayAbbreviation = (dateString, timezone = DEFAULT_TIMEZONE) => {
  if (!dateString) return '';
  const date = parseAsUTC(dateString);

  const formatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    timeZone: timezone
  });
  const dayStr = formatter.format(date);
  const dayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
  return dayAbbreviations[dayMap[dayStr]] || '';
};

/**
 * Get month name in Ukrainian
 * @param {number} monthIndex - Month index (0-11)
 * @returns {string} Month name
 */
export const getMonthName = (monthIndex) => {
  return monthNames[monthIndex] || '';
};

/**
 * Get month name in genitive case
 * @param {number} monthIndex - Month index (0-11)
 * @returns {string} Month name in genitive
 */
export const getMonthNameGenitive = (monthIndex) => {
  return monthNamesGenitive[monthIndex] || '';
};

/**
 * Format date for display in schedule (e.g., '15.02')
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted date (e.g., '15.02')
 */
export const formatShortDate = (dateString, timezone = DEFAULT_TIMEZONE) => {
  if (!dateString) return '';
  const date = parseAsUTC(dateString);
  return date.toLocaleDateString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    timeZone: timezone
  });
};

/**
 * Format date for display with year (e.g., '15.02.2026')
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted date with year
 */
export const formatFullDate = (dateString, timezone = DEFAULT_TIMEZONE) => {
  if (!dateString) return '';
  const date = parseAsUTC(dateString);
  return date.toLocaleDateString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: timezone
  });
};

/**
 * Get current time in a specific timezone
 * @param {string} timezone - IANA timezone string
 * @returns {Date} Current date adjusted for display purposes
 */
export const getCurrentTimeInTimezone = (timezone = DEFAULT_TIMEZONE) => {
  return new Date().toLocaleString('en-US', { timeZone: timezone });
};

/**
 * Check if a date is today in the user's timezone
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {string} timezone - IANA timezone string
 * @returns {boolean}
 */
export const isToday = (dateString, timezone = DEFAULT_TIMEZONE) => {
  const date = parseAsUTC(dateString);
  const now = new Date();

  const dateStr = date.toLocaleDateString('en-US', { timeZone: timezone });
  const nowStr = now.toLocaleDateString('en-US', { timeZone: timezone });

  return dateStr === nowStr;
};

/**
 * Check if a date is tomorrow in the user's timezone
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {string} timezone - IANA timezone string
 * @returns {boolean}
 */
export const isTomorrow = (dateString, timezone = DEFAULT_TIMEZONE) => {
  const date = parseAsUTC(dateString);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const dateStr = date.toLocaleDateString('en-US', { timeZone: timezone });
  const tomorrowStr = tomorrow.toLocaleDateString('en-US', { timeZone: timezone });

  return dateStr === tomorrowStr;
};

// Export constants for components that need them
export { monthNames, monthNamesGenitive, dayNames, dayAbbreviations, dayAbbreviationsMondayFirst };
